import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";

import { useBulkImport } from "@/hooks/use-users";
import { toast } from "sonner";

export function BulkImportPage() {
  const bulkImport = useBulkImport();
  const [secrets, setSecrets] = useState("");
  const [result, setResult] = useState<{
    parsed: number;
    created: number;
    updated: number;
    failed: number;
    errors: string[];
  } | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!secrets.trim()) {
      toast.error("Please paste ipsec.secrets content.");
      return;
    }

    try {
      const res = await bulkImport.mutateAsync({ secrets });
      setResult(res);
      toast.success(`Imported: ${res.created} created, ${res.updated} updated`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Import failed");
    }
  };

  return (
    <div className="mx-auto max-w-2xl space-y-6">
      <Button variant="ghost" size="sm" asChild className="-ml-3">
        <Link to="/">
          <ArrowLeft className="mr-2 h-4 w-4" /> Back to users
        </Link>
      </Button>

      <Card>
        <CardHeader className="space-y-1">
          <CardTitle className="text-xl">Bulk import</CardTitle>
          <CardDescription>
            Paste credentials in <code>ipsec.secrets</code> format. Existing users will have their
            passwords updated.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <Textarea
              value={secrets}
              onChange={(e) => setSecrets(e.target.value)}
              placeholder={`admin : EAP "secretpassword"\nmisha : EAP "anotherpassword"`}
              className="min-h-[240px] font-mono text-sm"
            />
            <Button type="submit" className="w-full h-11" disabled={bulkImport.isPending}>
              {bulkImport.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Import users
            </Button>
          </form>

          {result && (
            <div className="mt-6 space-y-4">
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                <ResultCard label="Parsed" value={result.parsed} />
                <ResultCard label="Created" value={result.created} variant="success" />
                <ResultCard label="Updated" value={result.updated} variant="info" />
                <ResultCard label="Failed" value={result.failed} variant="danger" />
              </div>
              {result.errors.length > 0 && (
                <div className="rounded-lg border border-destructive/20 bg-destructive/10 p-3">
                  <p className="mb-2 text-sm font-medium text-destructive">Errors</p>
                  <ul className="list-inside list-disc space-y-1 text-sm text-destructive">
                    {result.errors.map((err, idx) => (
                      <li key={idx}>{err}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function ResultCard({
  label,
  value,
  variant = "default",
}: {
  label: string;
  value: number;
  variant?: "default" | "success" | "info" | "danger";
}) {
  const variants = {
    default: "bg-muted text-foreground",
    success: "bg-emerald-500/10 text-emerald-700 dark:text-emerald-400",
    info: "bg-blue-500/10 text-blue-700 dark:text-blue-400",
    danger: "bg-destructive/10 text-destructive",
  };

  return (
    <div className={`flex flex-col items-center justify-center rounded-lg p-4 ${variants[variant]}`}>
      <span className="text-2xl font-bold">{value}</span>
      <span className="text-xs font-medium opacity-80">{label}</span>
    </div>
  );
}
